import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AddPost.css';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import TagIcon from '@mui/icons-material/Tag';
import PublicIcon from '@mui/icons-material/Public';
import CloseIcon from '@mui/icons-material/Close';
import { divIcon, type LatLngExpression } from 'leaflet';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';

import axiosInstance from '../../../main/initializers/axios';
import useGetUser from '../../../main/hooks/useGetUser';
import type { AddPostPropsDto, PostLocationOptionDto, PrivacyType } from './PostDto';
import { MAX_IMAGES } from './PostDto';

const MAX_CHARS = 500;
const EMOJI_OPTIONS = [
  '\u{1F600}',
  '\u{1F60D}',
  '\u{1F525}',
  '\u{1F389}',
  '\u{1F4CD}',
  '\u{1F37D}\u{FE0F}',
  '\u{1F3E8}',
  '\u{2615}',
  '\u{1F305}',
  '\u{1F4BC}',
  '\u{1F680}',
  '\u{1F44F}',
];

const LOCATION_ENDPOINT = '/api/feed/locations';
const NOMINATIM_SEARCH_URL = 'https://nominatim.openstreetmap.org/search';
const DEFAULT_LOCATION_CENTER: [number, number] = [41.3231, 19.4414];

const composerMapPin = divIcon({
  className: 'composer-map-pin-shell',
  html: '<span class="composer-map-pin"></span>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const MapViewportSync: React.FC<{ center: LatLngExpression }> = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    map.setView(center, map.getZoom(), { animate: true });
  }, [center, map]);

  return null;
};

const MapClickCapture: React.FC<{ onPick: (latitude: number, longitude: number) => void }> = ({ onPick }) => {
  useMapEvents({
    click: (event) => {
      onPick(event.latlng.lat, event.latlng.lng);
    },
  });

  return null;
};

const AddPost: React.FC<AddPostPropsDto> = ({ onAddPost, suggestedTags = [], locationOptions = [], onLocationSelect }) => {
  const { i18n } = useTranslation();
  const user = useGetUser();
  const [content, setContent] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [privacy, setPrivacy] = useState<PrivacyType>('public');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEmojiOpen, setIsEmojiOpen] = useState(false);
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<PostLocationOptionDto | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [customTagInput, setCustomTagInput] = useState('');
  const [remoteLocationResults, setRemoteLocationResults] = useState<PostLocationOptionDto[]>([]);
  const [isSearchingLocations, setIsSearchingLocations] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canSubmit = content.trim().length > 0 || images.length > 0;
  const isContentTooLong = content.length > MAX_CHARS;

  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setContent('');
    setImages([]);
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setImagePreviews([]);
    setSelectedLocation(null);
    setLocationSearch('');
    setSelectedTags([]);
    setCustomTagInput('');
    setRemoteLocationResults([]);
    setIsEmojiOpen(false);
    setIsTagOpen(false);
    setIsLocationOpen(false);
  };

  const persistLocation = async (location: PostLocationOptionDto | null) => {
    if (!location) return null;
    if (typeof location.locationId === 'number') return location;
    if (typeof location.latitude !== 'number' || typeof location.longitude !== 'number') return location;

    const response = await axiosInstance.post(LOCATION_ENDPOINT, {
      name: location.label,
      type: location.type || 'Place',
      lat: location.latitude,
      lng: location.longitude,
    });

    const saved = response.data as { id: number; name: string; type?: string; lat?: number | null; lng?: number | null };
    return {
      ...location,
      id: `location-${saved.id}`,
      label: saved.name,
      meta: saved.type || location.meta,
      locationId: saved.id,
      latitude: saved.lat ?? location.latitude ?? null,
      longitude: saved.lng ?? location.longitude ?? null,
      type: saved.type || location.type,
    } satisfies PostLocationOptionDto;
  };

  const handleSubmit = async () => {
    if (!canSubmit || isContentTooLong) return;

    const persistedLocation = await persistLocation(selectedLocation);
    if (persistedLocation) {
      setSelectedLocation(persistedLocation);
    }

    await onAddPost(content.trim(), images, {
      privacy,
      tags: selectedTags.join(', '),
      locationName: persistedLocation?.label ?? selectedLocation?.label ?? null,
      locationId: persistedLocation?.locationId ?? selectedLocation?.locationId ?? null,
    });

    resetForm();
    setIsExpanded(false);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const newFiles = Array.from(e.target.files);
    const remainingSlots = MAX_IMAGES - images.length;

    if (newFiles.length > remainingSlots) {
      alert(i18n.t('mainLanding.addPost.maxImages', { defaultValue: 'You can upload a maximum of {{count}} photos.', count: MAX_IMAGES }));
      e.currentTarget.value = '';
      return;
    }

    const validFiles = newFiles.slice(0, remainingSlots);
    setImages((prev) => [...prev, ...validFiles]);
    setImagePreviews((prev) => [...prev, ...validFiles.map((file) => URL.createObjectURL(file))]);
    e.currentTarget.value = '';
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const openFileSelector = () => fileInputRef.current?.click();

  const appendEmoji = (emoji: string) => {
    setContent((prev) => `${prev}${emoji}`);
    setIsEmojiOpen(false);
    setIsExpanded(true);
  };

  const applyTag = (tag: string) => {
    const normalized = tag.startsWith('#') ? tag : `#${tag}`;
    setSelectedTags((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
    setContent((prev) => {
      const trimmed = prev.trim();
      if (!trimmed) return `${normalized} `;
      return trimmed.includes(normalized) ? prev : `${trimmed} ${normalized} `;
    });
    setIsTagOpen(false);
    setIsExpanded(true);
  };

  const addCustomTag = () => {
    const cleanValue = customTagInput.trim().replace(/^#/, '');
    if (!cleanValue) return;
    applyTag(cleanValue);
    setCustomTagInput('');
  };

  const filteredSavedLocations = locationOptions.filter((item) => {
    const term = locationSearch.trim().toLowerCase();
    if (!term) return true;
    return `${item.label} ${item.meta ?? ''}`.toLowerCase().includes(term);
  });

  const locationResults = useMemo(() => {
    const merged = [...filteredSavedLocations, ...remoteLocationResults];
    const seen = new Set<string>();
    return merged.filter((item) => {
      const key = `${item.label.trim().toLowerCase()}::${Math.round((item.latitude ?? 0) * 1000)}::${Math.round((item.longitude ?? 0) * 1000)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [filteredSavedLocations, remoteLocationResults]);

  useEffect(() => {
    if (!isLocationOpen) return;

    const controller = new AbortController();
    const term = locationSearch.trim();
    const timer = window.setTimeout(async () => {
      if (term.length < 3) {
        setRemoteLocationResults([]);
        setIsSearchingLocations(false);
        return;
      }

      try {
        setIsSearchingLocations(true);
        const query = new URLSearchParams({
          format: 'jsonv2',
          q: term,
          limit: '6',
        });

        const response = await fetch(`${NOMINATIM_SEARCH_URL}?${query.toString()}`, {
          signal: controller.signal,
          headers: {
            Accept: 'application/json',
          },
        });

        const payload = (await response.json()) as Array<{ place_id: number; display_name: string; lat: string; lon: string; type?: string }>;
        setRemoteLocationResults(
          (payload ?? []).map((item) => {
            const [label, ...rest] = item.display_name.split(',');
            return {
              id: `remote-${item.place_id}`,
              label: label?.trim() || item.display_name,
              meta: rest.join(',').trim(),
              latitude: Number(item.lat),
              longitude: Number(item.lon),
              type: item.type || 'Place',
              mapTargetId: null,
            } satisfies PostLocationOptionDto;
          }),
        );
      } catch {
        if (!controller.signal.aborted) {
          setRemoteLocationResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearchingLocations(false);
        }
      }
    }, 320);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [isLocationOpen, locationSearch]);

  const mapCenter = useMemo<LatLngExpression>(() => {
    if (typeof selectedLocation?.latitude === 'number' && typeof selectedLocation?.longitude === 'number') {
      return [selectedLocation.latitude, selectedLocation.longitude];
    }

    const firstWithCoordinates = locationResults.find((item) => typeof item.latitude === 'number' && typeof item.longitude === 'number');
    if (firstWithCoordinates && typeof firstWithCoordinates.latitude === 'number' && typeof firstWithCoordinates.longitude === 'number') {
      return [firstWithCoordinates.latitude, firstWithCoordinates.longitude];
    }

    return DEFAULT_LOCATION_CENTER;
  }, [locationResults, selectedLocation]);

  const chooseLocation = (location: PostLocationOptionDto) => {
    setSelectedLocation(location);
    if (location.mapTargetId) {
      onLocationSelect?.(location);
    }
  };

  const useSelectedLocation = () => {
    if (!selectedLocation) return;
    if (selectedLocation.mapTargetId) {
      onLocationSelect?.(selectedLocation);
    }
    setIsLocationOpen(false);
    setIsExpanded(true);
  };

  const handleMapPick = (latitude: number, longitude: number) => {
    const label = locationSearch.trim() || i18n.t('mainLanding.addPost.pinnedLocation', { defaultValue: 'Pinned location' });
    setSelectedLocation({
      id: `draft-${latitude}-${longitude}`,
      label,
      meta: i18n.t('mainLanding.addPost.customMapPoint', { defaultValue: 'Custom map point' }),
      latitude,
      longitude,
      type: 'Place',
      mapTargetId: null,
    });
  };

  const getCharCountColor = () => {
    const percentage = (content.length / MAX_CHARS) * 100;
    if (percentage > 90) return '#ef4444';
    if (percentage > 75) return '#f59e0b';
    return '#6b7280';
  };

  const avatarLetter = (user?.userName?.trim()?.[0] ?? 'U').toUpperCase();

  return (
    <div className={`add-post-modern ${isExpanded ? 'expanded' : ''}`}>
      <div className="post-header">
        <div className="user-avatar-post">{avatarLetter}</div>
        <div className="post-input-header" onClick={() => setIsExpanded(true)}>
          <textarea
            className="post-textarea-modern"
            placeholder={i18n.t('mainLanding.addPost.placeholder', { defaultValue: 'What is happening around you? Share it with us...' })}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={isExpanded ? 4 : 1}
            maxLength={MAX_CHARS}
          />

          {!isExpanded && (
            <div className="post-actions-mini">
              <button type="button" className="action-mini" title={i18n.t('mainLanding.addPost.addPhoto', { defaultValue: 'Add photo' })} onClick={openFileSelector}>
                <AddPhotoAlternateIcon />
              </button>
              <button type="button" className="action-mini" title={i18n.t('mainLanding.addPost.addEmoji', { defaultValue: 'Add emoji' })} onClick={() => setIsEmojiOpen(true)}>
                <EmojiEmotionsIcon />
              </button>
              <button type="button" className="action-mini" title={i18n.t('mainLanding.addPost.addLocation', { defaultValue: 'Add location' })} onClick={() => setIsLocationOpen(true)}>
                <LocationOnIcon />
              </button>
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <>
          {imagePreviews.length > 0 && (
            <div className="image-grid-modern">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="image-preview-modern">
                  <img src={preview} alt={`Preview ${index + 1}`} />
                  <button type="button" className="remove-image-modern" onClick={() => removeImage(index)}>
                    <CloseIcon fontSize="small" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="post-options">
            <div className="privacy-selector">
              <PublicIcon fontSize="small" />
              <select value={privacy} onChange={(e) => setPrivacy(e.target.value as PrivacyType)} className="privacy-select">
                <option value="public">{i18n.t('mainLanding.addPost.public', { defaultValue: 'Public' })}</option>
                <option value="friends">{i18n.t('mainLanding.addPost.friends', { defaultValue: 'Friends' })}</option>
                <option value="private">{i18n.t('mainLanding.addPost.private', { defaultValue: 'Private' })}</option>
              </select>
            </div>

            {selectedLocation ? (
              <button type="button" className="location-pill" onClick={() => setIsLocationOpen(true)}>
                <LocationOnIcon fontSize="small" />
                <span>{selectedLocation.label}</span>
              </button>
            ) : null}

            <div className="char-count-modern" style={{ color: getCharCountColor() }}>
              {content.length}/{MAX_CHARS}
            </div>
          </div>

          <div className="post-footer-modern">
            <div className="action-buttons-modern">
              <button type="button" className="action-btn-modern" onClick={openFileSelector} disabled={images.length >= MAX_IMAGES}>
                <AddPhotoAlternateIcon />
                <span>{i18n.t('mainLanding.addPost.photo', { defaultValue: 'Photo' })}</span>
                {images.length > 0 && <span className="photo-count-badge">{images.length}</span>}
              </button>

              <button type="button" className="action-btn-modern" onClick={() => setIsEmojiOpen(true)}>
                <EmojiEmotionsIcon />
                <span>{i18n.t('mainLanding.addPost.emoji', { defaultValue: 'Emoji' })}</span>
              </button>

              <button type="button" className="action-btn-modern" onClick={() => setIsLocationOpen(true)}>
                <LocationOnIcon />
                <span>{i18n.t('mainLanding.addPost.location', { defaultValue: 'Location' })}</span>
              </button>

              <button type="button" className="action-btn-modern" onClick={() => setIsTagOpen(true)}>
                <TagIcon />
                <span>{i18n.t('mainLanding.addPost.tag', { defaultValue: 'Tag' })}</span>
              </button>
            </div>

            <div className="submit-buttons">
              <button type="button" className="cancel-btn-modern" onClick={() => setIsExpanded(false)}>
                {i18n.t('mainLanding.actions.cancel', { defaultValue: 'Cancel' })}
              </button>

              <button
                type="button"
                className={`submit-btn-modern ${canSubmit && !isContentTooLong ? 'active' : ''}`}
                onClick={handleSubmit}
                disabled={!canSubmit || isContentTooLong}
              >
                {i18n.t('mainLanding.addPost.submit', { defaultValue: 'Post' })}
              </button>
            </div>
          </div>
        </>
      )}

      {selectedTags.length > 0 && (
        <div className="selected-tags-row">
          {selectedTags.map((tag) => (
            <button
              key={tag}
              type="button"
              className="selected-tag-chip"
              onClick={() => setSelectedTags((prev) => prev.filter((item) => item !== tag))}
            >
              {tag}
            </button>
          ))}
        </div>
      )}

      {isEmojiOpen && (
        <div className="composer-modal-backdrop" onClick={() => setIsEmojiOpen(false)}>
          <div className="composer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="composer-modal-header">
              <h4>{i18n.t('mainLanding.addPost.pickEmoji', { defaultValue: 'Choose emoji' })}</h4>
              <button type="button" className="composer-modal-close" onClick={() => setIsEmojiOpen(false)}>
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="emoji-grid">
              {EMOJI_OPTIONS.map((emoji) => (
                <button key={emoji} type="button" className="emoji-option" onClick={() => appendEmoji(emoji)}>
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {isTagOpen && (
        <div className="composer-modal-backdrop" onClick={() => setIsTagOpen(false)}>
          <div className="composer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="composer-modal-header">
              <h4>{i18n.t('mainLanding.addPost.trendingTags', { defaultValue: 'Trending tags' })}</h4>
              <button type="button" className="composer-modal-close" onClick={() => setIsTagOpen(false)}>
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <div className="tag-suggestions">
              <div className="tag-create-row">
                <input
                  type="text"
                  className="tag-create-input"
                  placeholder={i18n.t('mainLanding.addPost.newTagPlaceholder', { defaultValue: 'Add a tag' })}
                  value={customTagInput}
                  onChange={(e) => setCustomTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addCustomTag();
                    }
                  }}
                />
                <button type="button" className="tag-create-btn" onClick={addCustomTag}>
                  {i18n.t('mainLanding.addPost.addTag', { defaultValue: 'Add tag' })}
                </button>
              </div>
              {suggestedTags.length === 0 ? (
                <p className="composer-modal-empty">{i18n.t('mainLanding.addPost.noSuggestedTags', { defaultValue: 'No suggested tags.' })}</p>
              ) : (
                suggestedTags.map((tag) => (
                  <button key={tag} type="button" className="tag-suggestion-chip" onClick={() => applyTag(tag)}>
                    #{tag.replace(/^#/, '')}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {isLocationOpen && (
        <div className="composer-modal-backdrop" onClick={() => setIsLocationOpen(false)}>
          <div className="composer-modal composer-modal--location" onClick={(e) => e.stopPropagation()}>
            <div className="composer-modal-header">
              <h4>{i18n.t('mainLanding.addPost.pickLocation', { defaultValue: 'Choose location' })}</h4>
              <button type="button" className="composer-modal-close" onClick={() => setIsLocationOpen(false)}>
                <CloseIcon fontSize="small" />
              </button>
            </div>
            <input
              type="text"
              className="location-search-input"
              placeholder={i18n.t('mainLanding.addPost.searchLocation', { defaultValue: 'Search location' })}
              value={locationSearch}
              onChange={(e) => setLocationSearch(e.target.value)}
            />
            <div className="location-map-preview">
              <LocationOnIcon fontSize="small" />
              <span>{selectedLocation?.label ?? i18n.t('mainLanding.addPost.selectMapPoint', { defaultValue: 'Choose a point on the map' })}</span>
            </div>
            <p className="location-map-hint">{i18n.t('mainLanding.addPost.mapHint', { defaultValue: 'Search for a place or click on the map to drop a marker.' })}</p>
            <div className="location-map-canvas">
              <MapContainer center={mapCenter} zoom={13} minZoom={3} maxZoom={19} scrollWheelZoom className="location-leaflet-map">
                <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} maxNativeZoom={19} />
                <MapViewportSync center={mapCenter} />
                <MapClickCapture onPick={handleMapPick} />
                {locationResults
                  .filter((item) => typeof item.latitude === 'number' && typeof item.longitude === 'number')
                  .map((item) => (
                    <Marker
                      key={item.id}
                      position={[item.latitude as number, item.longitude as number]}
                      icon={composerMapPin}
                      eventHandlers={{ click: () => chooseLocation(item) }}
                    />
                  ))}
                {selectedLocation && typeof selectedLocation.latitude === 'number' && typeof selectedLocation.longitude === 'number' ? (
                  <Marker position={[selectedLocation.latitude, selectedLocation.longitude]} icon={composerMapPin} />
                ) : null}
              </MapContainer>
            </div>
            <div className="location-results">
              {isSearchingLocations ? <p className="composer-modal-empty">{i18n.t('mainLanding.addPost.searchingLocations', { defaultValue: 'Searching places...' })}</p> : null}
              {!isSearchingLocations && locationResults.length === 0 ? (
                <p className="composer-modal-empty">{i18n.t('mainLanding.addPost.noLocationsFound', { defaultValue: 'No locations found yet.' })}</p>
              ) : null}
              {locationResults.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`location-result-item ${selectedLocation?.id === option.id ? 'active' : ''}`}
                  onClick={() => chooseLocation(option)}
                >
                  <strong>{option.label}</strong>
                  {option.meta ? <span>{option.meta}</span> : null}
                </button>
              ))}
            </div>
            <div className="composer-modal-actions">
              <button type="button" className="cancel-btn-modern" onClick={() => setIsLocationOpen(false)}>
                {i18n.t('mainLanding.actions.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button type="button" className={`submit-btn-modern ${selectedLocation ? 'active' : ''}`} onClick={useSelectedLocation} disabled={!selectedLocation}>
                {i18n.t('mainLanding.addPost.useLocation', { defaultValue: 'Use location' })}
              </button>
            </div>
          </div>
        </div>
      )}

      <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" multiple style={{ display: 'none' }} />
    </div>
  );
};

export default AddPost;
